import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { CalendarClock, FileCheck2, Building2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { faqs } from "../mock";

function PageHero({ eyebrow, title, subtitle }) {
  return (
    <section className="relative pt-[76px] bg-[#F4F7FB] diag-lines">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <span className="eyebrow">{eyebrow}</span>
        <h1 className="font-display text-5xl md:text-6xl text-brand mt-3">{title}</h1>
        {subtitle && (
          <p className="text-slate-600 text-lg mt-4 max-w-2xl">{subtitle}</p>
        )}
      </div>
    </section>
  );
}

const points = [
  {
    icon: CalendarClock,
    title: "Who Needs ELDT?",
    text: "Anyone who obtained a commercial learner's permit (CLP) on or after February 7, 2022 must complete ELDT before taking their CDL skills or knowledge test.",
  },
  {
    icon: FileCheck2,
    title: "100% Online Theory",
    text: "Federally mandated theory training — vehicle systems, safety, regulations, and more — delivered entirely online with no classroom, so you can complete it before you test.",
  },
  {
    icon: Building2,
    title: "How Results Are Used",
    text: "On completion, results are submitted to the Training Provider Registry. Testing centers retrieve them to confirm you’re eligible before providing testing services.",
  },
];

export default function WhyELDT() {
  return (
    <main>
      <PageHero
        eyebrow="Understanding The Rules"
        title="Why ELDT?"
        subtitle="Entry-Level Driver Training is a federal requirement — and with CDL Academy it's completed 100% online, at your own pace. Here's what it is, who needs it, and how it fits into getting your CDL."
      />

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {points.map((p) => (
              <div
                key={p.title}
                className="card-lift bg-white border border-slate-200 rounded-2xl p-7 soft-shadow"
              >
                <div className="w-12 h-12 rounded-lg bg-[#16365C]/8 flex items-center justify-center mb-5">
                  <p.icon className="w-6 h-6 text-steel" />
                </div>
                <h3 className="font-head font-semibold text-slate-900 text-lg mb-2">
                  {p.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#F4F7FB]">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-12">
            <span className="eyebrow">Questions &amp; Answers</span>
            <h2 className="font-display text-4xl md:text-5xl text-brand mt-3">
              Frequently Asked
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`item-${i}`}
                className="bg-white border border-slate-200 rounded-xl px-6 data-[state=open]:border-brand shadow-sm"
              >
                <AccordionTrigger className="font-head font-semibold text-slate-900 text-left text-base hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-500 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl text-brand">
            Still Have Questions?
          </h2>
          <p className="text-slate-500 mt-3">
            Our in-house support team is here to help you get on the road.
          </p>
          <Link
            to="/contact"
            className="btn-brand rounded-md px-8 py-4 mt-7 inline-flex items-center gap-2"
          >
            Contact Us <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </main>
  );
}
